import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults
} from 'axios';
import { startCase } from 'lodash';

import envConfig, { APP_STAGE_DEVELOPMENT, APP_STAGE_STAGING } from '@config';
import { GlobalLoginModal } from '@elements';
import * as Sentry from '@sentry/react';
import { useAccount } from '@store/account';
import { showNotification } from '@ui';
import { customerAccountUrl, statusPageUrl } from '@utils';

const userFriendlyMessage = (message: string, title?: string): void => {
  const finalMessage = message
    ? message.charAt(0).toUpperCase() + message.slice(1)
    : '';
  showNotification({
    title: title || 'Error',
    message: finalMessage,
    color: 'red',
    autoClose: 5000,
    radius: 'md',
    withCloseButton: true
  });
};

export class APIClient {
  axiosInstance = axios.create({
    baseURL: ''
  });

  constructor(baseUrl: string, headers?: Record<string, unknown>) {
    const config: CreateAxiosDefaults = {
      baseURL: baseUrl,
      // `timeout` specifies the number of milliseconds before the request times out.
      timeout: 310 * 1000,
      // withCredentials indicates whether or not cross-site Access-Control requests should be made using credentials
      withCredentials: true,
      // `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
      xsrfCookieName: 'csrftoken',
      // `xsrfHeaderName` is the name of the http header that carries the xsrf token value
      xsrfHeaderName: 'X-CSRFToken',
      /* Required since axios 1.6.2. It used to attach the xsrf header whenever
         `withCredentials` was set; now it only does so for same-origin requests
         unless `withXSRFToken` is explicitly true. INSURANCE_API is always a
         different origin than the app (api.joinditto.in vs the app domain,
         :8000 vs :3000 locally), so without this the X-CSRFToken header is
         dropped and Django rejects every mutating request. */
      withXSRFToken: true,
      headers: headers
        ? headers
        : {
            // content type
            'Content-Type': 'application/json'
          }
    };

    this.axiosInstance = axios.create(config);

    this.axiosInstance.interceptors.request.use(
      (requestConfig) => requestConfig,
      (requestError) => {
        return Promise.reject(requestError);
      }
    );

    this.axiosInstance.interceptors.request.use((config: any) => {
      // request config overrides can be applied here
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (
          envConfig.APP_STAGE === APP_STAGE_DEVELOPMENT ||
          envConfig.APP_STAGE === APP_STAGE_STAGING
        ) {
          //don't remove
          console.log({ response });
        }
        return response.data;
      },
      (error) => {
        // to avoid multiple notifications for the same error
        error.notified = true;

        /*  Potential errors : 401, 403, 500
          401 vs 403: https://stackoverflow.com/questions/3297048/403-forbidden-vs-401-unauthorized-http-responses
        */
        if (error.response && error.response !== null) {
          if (error.response.status >= 500) {
            userFriendlyMessage(
              'Looks like an issue at server! Our team is fixing it.',
              'Server error'
            );
          } else if (error.response.status === 403) {
            // authentication error
            let message = error.response.data
              ? error.response.data['message'] || error.response.data['detail']
              : undefined;
            message = message ? message : 'Please login and retry!.';
            /**
             * Don't show the toast if the current page is a status page/ proposal page
             */
            if (!statusPageUrl(error.config.url)) {
              userFriendlyMessage(message, 'Error');
            }
            // Don't show registration dialog from customer account urls, redirect to login instead (done in LazyAccessControl)
            if (!customerAccountUrl(window.location.pathname)) {
              // Invoking the global modal.
              GlobalLoginModal.toggle({
                isShown: true,
                onAuthSuccess: () => {
                  // Delay just to make sure user check the toast
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                }
              });
            } else {
              useAccount.getState().reset();
            }
          } else if (error.response.status === 401) {
            /**
             * Unauthenticated request. Check if the header is present & if yes, the token is invalidated. User needs to login again
             */
            userFriendlyMessage('Please login and try again', 'Error');
          } else if (error.response.status === 400) {
            const data = error.response.data;
            const errorKeys = data && data.errors ? Object.keys(data.errors) : [];

            if (errorKeys.length > 0) {
              errorKeys.forEach((key) => {
                const isOtherErrors = key === 'other_errors';

                if (isOtherErrors) {
                  const otherErrors = data.errors[key] || [];
                  otherErrors.forEach((error: any) => {
                    userFriendlyMessage(error, 'Error');
                  });
                } else {
                  const message = `${startCase(key)} - ${data.errors[key]}`;
                  userFriendlyMessage(message, 'Error');
                }
              });
            } else {
              userFriendlyMessage('Not able to process your request.', 'Error');
            }
          } else if (error.response.status === 404) {
            // remove or change the meassage in prod
            userFriendlyMessage('Please check the URL', 'Error');
          } else {
            error.notified = false;
          }
        } else {
          // remove once 499 issue is resolved with care
          console.log(error.message);
          console.log(error.code);
          Sentry.captureException(error);

          // network error
          userFriendlyMessage(
            'Not able to connect our servers due to a network error. Please try again!',
            'Error'
          );
          error.notified = true;
        }
        // send error forward for any further handling
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetches data from given url
   */
  get<T, R = AxiosResponse<T>>(url: string, params?: any): Promise<R> {
    return this.axiosInstance.get(url, { params });
  }

  /**
   * post given data to url
   */
  post<T, B, R = AxiosResponse<T>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.axiosInstance.post(url, data, config);
  }

  /**
   * Updates data
   */
  update<T, B, R = AxiosResponse<T>>(url: string, data?: B): Promise<R> {
    return this.axiosInstance.patch(url, data);
  }

  /**
   * Delete
   */
  delete<T, B, R = AxiosResponse<T>>(url: string, data?: B): Promise<R> {
    return this.axiosInstance.delete(url, { data });
  }
}

export default APIClient;
